import React from "react";
import axios, { AxiosError } from "axios";
import {
  B2B_PORTAL_JWT_SECRET_KEY,
  B2B_PORTAL_CSRF_TOKEN,
  B2B_PORTAL_AUTH_REDIRECT_TO,
  B2B_PORTAL_UNAUTHORIZED_PATH,
} from "redmond";
import { useCorpRedirects } from "@capone/common";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";
import { createRequestLog, createResponseLog } from "../Logger";
import config from "../../utils/config";
import { UNPROTECTED_PATHS } from "../../utils/urlPaths";

interface IAxiosInterceptors {
  children?: React.ReactNode;
}

export const mcLeanAxiosInstance = axios.create();

const AxiosInterceptors = ({ children }: IAxiosInterceptors) => {
  const location = useLocation();
  const navigate = useNavigate();

  useCorpRedirects(mcLeanAxiosInstance, config.TENANT);
  const getCookie = (name: string) => {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    if (match) return match[2];
    return undefined;
  };

  const getCsrfToken = () => getCookie(B2B_PORTAL_CSRF_TOKEN);

  const setAuthRedirectUrl = (url: string) =>
    sessionStorage.setItem(B2B_PORTAL_AUTH_REDIRECT_TO, url);

  const responseError = (error: AxiosError) => {
    if (error.response?.status === 401) {
      const isUnprotectedPath = UNPROTECTED_PATHS.some((path) => {
        const pathWithoutSlash = path.endsWith('/') ? path.slice(0, -1) : path;
        return location.pathname.startsWith(path) || location.pathname === pathWithoutSlash;
      });

      if (!isUnprotectedPath) {
        setAuthRedirectUrl(
          `${location.pathname}${location.search}${location.hash}`
        );
        navigate(B2B_PORTAL_UNAUTHORIZED_PATH);
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  };
  const responseInterceptor = axios.interceptors.response.use(
    (res) => res,
    responseError
  );

  React.useEffect(() => {
    const mcleanResponseInterceptor =
      mcLeanAxiosInstance.interceptors.response.use(
        (res) => {
          createResponseLog(res);
          return res;
        },
        (error) => Promise.reject(error)
      );

    const mcleanRequestInterceptor =
      mcLeanAxiosInstance.interceptors.request.use(
        (req) => {
          const csrfToken = getCsrfToken();

          // TODO: remove GCP token
          const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(
              B2B_PORTAL_JWT_SECRET_KEY
            )}`,
            ...(csrfToken && { "H-Csrf-Token": csrfToken }),
          };

          req.headers.set({ ...req.headers, ...headers });

          createRequestLog(req);
          return req;
        },
        (error) => Promise.reject(error)
      );

    // runs when component unmount
    return () => {
      mcLeanAxiosInstance.interceptors.request.eject(mcleanRequestInterceptor);
      mcLeanAxiosInstance.interceptors.response.eject(
        mcleanResponseInterceptor
      );
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return <>{children}</>;
};

export default AxiosInterceptors;
